"use client";

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_ORDER_REPORT_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";
import { showToast } from "../../../components/custom/custom-toast";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import { CustomerService, Customer } from "../../customer/services/customer-service";
import ReconciliationService, {
  ITEM_DEBT_ENTRY_LABEL,
  ITEM_DEBT_ENTRY_TYPE,
  ItemDebtLog,
  ReconciliationReport,
} from "../services/reconciliation-service";
import CreateDebtAdjustmentRequestModal from "../../adjustment-request/components/create-debt-adjustment-request-modal";
import Select from "react-select";

// DateInputCustom nhận/trả chuỗi dd/MM/yyyy, còn API dùng yyyy-MM-dd → phải đổi qua lại.
const toDMY = (ymd: string) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmt = (v: number) => numberFmt.format(Number(v) || 0);
/** 0 hiển thị rỗng cho bảng đỡ rối — cửa hàng đang làm tay cũng để trống ô không có số. */
const cell = (v: number) => (v ? fmt(v) : "");
const dayLabel = (ymd: string) => Number(ymd.slice(8, 10));

const getDefaultDates = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const f = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: f(first), to: f(now) };
};

const ReconciliationReportPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [customerId, setCustomerId] = useState<number | "">("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [logs, setLogs] = useState<ItemDebtLog[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [createRequestCategory, setCreateRequestCategory] = useState<string | null>(null);
  const [showCreateRequest, setShowCreateRequest] = useState(false);

  const { fetch: fetchReport } = ReconciliationService.report();
  const { fetch: fetchLogs } = ReconciliationService.debtLogs();
  const { fetch: listCustomers } = CustomerService.list();

  const reportRef = useRef(fetchReport);
  const logsRef = useRef(fetchLogs);
  const customerRef = useRef(listCustomers);
  useEffect(() => { reportRef.current = fetchReport; }, [fetchReport]);
  useEffect(() => { logsRef.current = fetchLogs; }, [fetchLogs]);
  useEffect(() => { customerRef.current = listCustomers; }, [listCustomers]);

  // Danh sách khách để chọn. Báo cáo đối soát chỉ có nghĩa với khách KIỂM ĐẾM nên lọc luôn cho gọn.
  useEffect(() => {
    (async () => {
      const res = await customerRef.current({ limit: 500, status: 1 });
      if (res && isSuccess(res)) {
        const list = ((res.data as any)?.list || []) as Customer[];
        setCustomers(list.filter((c) => Number((c as any).requires_counting) === 1));
      }
    })();
  }, []);

  const loadReport = useCallback(async () => {
    if (!customerId) { setReport(null); return; }
    setIsLoading(true);
    try {
      const res = await reportRef.current({
        customer_id: Number(customerId), from_date: fromDate, to_date: toDate,
      });
      if (res && isSuccess(res)) setReport(res.data as ReconciliationReport);
      const logRes = await logsRef.current({
        customer_id: Number(customerId), from_date: fromDate, to_date: toDate, limit: 300,
      });
      if (logRes && isSuccess(logRes)) setLogs((logRes.data as ItemDebtLog[]) || []);
    } catch (e) {
      console.error("Failed to load reconciliation report:", e);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, fromDate, toDate]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  // Xuất Excel: BE tạo và trả file ngay (1 khách × 1 kỳ nhỏ, không chạy nền) — cùng khách + kỳ đang xem.
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    if (!customerId) return;
    setExporting(true);
    try {
      const token = CookieUtils.getCurrentUser().access_token ?? "";
      const res = await axios.get(`${ApiConfig.API_URL}/api/${API_ORDER_REPORT_ROUTER.API_CUSTOMER_RECONCILIATION_EXPORT}`, {
        params: { customer_id: Number(customerId), from_date: fromDate, to_date: toDate },
        headers: {
          Authorization: `Bearer ${token}`,
          "x-svc-id": ApiConfig.PROJECT_ID.ORDER_SERVICE,
          platform: "1",
        },
        responseType: "blob",
      });
      const disposition = String(res.headers["content-disposition"] || "");
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? decodeURIComponent(match[1]) : `doi-soat_${fromDate}_${toDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      // Lỗi trả về dạng blob JSON → đọc ra để hiện đúng thông báo của BE (vd quá 93 ngày).
      let message = "Xuất Excel thất bại";
      try {
        const body = JSON.parse(await e?.response?.data?.text());
        if (body?.message) message = body.message;
      } catch {}
      showToast({ message, type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const customerName = useMemo(
    () => customers.find((c) => Number(c.id) === Number(customerId))?.name || "",
    [customers, customerId],
  );

  const days = report?.days || [];
  const totals = report?.totals;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center text-white text-lg">
            ⚖️
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Đối soát số lượng theo khách hàng</h1>
            <p className="text-sm text-slate-500">
              Dơ (nhận vào) / Sạch (trả ra) theo từng loại đồ · chỉ tính đơn kiểm đếm
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Khách hàng</label>
            <Select
              placeholder="-- Chọn khách hàng --"
              options={customers.map((c) => ({
                label: `${c.name} ${c.phone ? `(${c.phone})` : ""}`,
                value: c.id
              }))}
              value={
                customerId
                  ? {
                      label: `${customers.find(c => c.id === customerId)?.name} ${customers.find(c => c.id === customerId)?.phone ? `(${customers.find(c => c.id === customerId)?.phone})` : ""}`,
                      value: customerId
                    }
                  : null
              }
              onChange={(option: any) => setCustomerId(option ? option.value : "")}
              isClearable
              isSearchable
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: "0.5rem", // rounded-lg
                  borderColor: "#e2e8f0", // border (slate-200)
                  minHeight: "38px",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#cbd5e1" },
                }),
              }}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Từ ngày</label>
            <DateInputCustom
              type="date"
              value={toDMY(fromDate)}
              onChange={(d) => setFromDate(toYMD(d ?? ""))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Đến ngày</label>
            <DateInputCustom
              type="date"
              value={toDMY(toDate)}
              onChange={(d) => setToDate(toYMD(d ?? ""))}
              className="w-full"
            />
          </div>
        </div>

        <p className="mt-3 text-xs text-slate-500 italic">
          Báo cáo chỉ tính <b>đơn kiểm đếm</b> (khách yêu cầu đếm từng loại đồ với khách ở cả hai đầu).
          Đơn thường của cùng khách không xuất hiện ở đây. Nợ tính theo <b>khách toàn hệ thống</b> — nợ phát
          sinh ở chi nhánh nào trả ở chi nhánh nào cũng được.
        </p>
      </div>

      {!customerId && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          Chọn khách hàng để xem báo cáo đối soát.
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          Đang tải...
        </div>
      )}

      {!!customerId && !isLoading && report && (
        <>
          {/* Ô tổng */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SummaryCard label="Tổng Dơ (nhận)" value={fmt(totals?.dirty || 0)} unit="cái" />
            <SummaryCard
              label={totals?.clean_returned ? `Tổng Sạch (giao) · gồm trả nợ ${fmt(totals.clean_returned)}` : "Tổng Sạch (giao)"}
              value={fmt(totals?.clean || 0)}
              unit="cái"
            />
            <SummaryCard
              label="Chênh trong kỳ"
              value={fmt((totals?.clean || 0) - (totals?.dirty || 0))}
              unit="cái"
              tone="rose"
            />
            <SummaryCard label="Nợ cuối kỳ" value={fmt(totals?.closing_debt || 0)} unit="cái" tone="amber" />
            <SummaryCard label="Số kg đã giao" value={fmt(report.weight_total)} unit="kg" />
          </div>

          {/* Giải thích chênh — phần quan trọng nhất để không ai nghĩ báo cáo sai */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">
              Chênh lệch giải thích từ đâu ({fmt((totals?.dirty || 0) - (totals?.clean || 0))} cái)
            </h3>
            <div className="flex flex-wrap gap-2 text-sm">
              <Chip label="Đang xử lý cuối kỳ" value={totals?.wip_close || 0} tone="slate" />
              <Chip label="Đang xử lý đầu kỳ" value={-(totals?.wip_open || 0)} tone="slate" />
              <Chip label="Nợ khách" value={totals?.owe || 0} tone="amber" />
              <Chip label="Đã trả nợ" value={-(totals?.returned || 0)} tone="emerald" />
              <Chip label="Thất thoát" value={totals?.lost || 0} tone="rose" />
              <Chip label="Hàng không chuẩn" value={totals?.non_standard || 0} tone="orange" />
              {!!totals?.adjust && <Chip label="Điều chỉnh" value={totals.adjust} tone="violet" />}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              <b>Đang xử lý</b> = đơn đã nhận nhưng chưa giao tính tới mốc đó (đơn gối đầu kỳ) — không phải
              mất đồ.{" "}
              {report.is_balanced ? (
                <span className="text-emerald-600 font-medium">✓ Số liệu cân khớp.</span>
              ) : (
                <span className="text-rose-600 font-medium">
                  ⚠ Có loại đồ chưa cân — xem cột "Lệch" trong bảng.
                </span>
              )}
            </p>
          </div>

          {/* Bảng chính — dựng đúng dạng file Excel đang làm tay */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">
                {customerName} — {toDMY(report.from_date)} → {toDMY(report.to_date)}
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{report.order_count} đơn kiểm đếm</span>
                <button
                  onClick={exportExcel}
                  disabled={exporting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-green-600 text-green-700 bg-white hover:bg-green-50 transition disabled:opacity-60"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                  </svg>
                  {exporting ? "Đang xuất..." : "Xuất Excel"}
                </button>
                <button
                  onClick={() => { setCreateRequestCategory(null); setShowCreateRequest(true); }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 transition"
                >
                  + Tạo yêu cầu điều chỉnh công nợ
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10">Mặt hàng</th>
                    <th className="px-2 py-2">Sạch/Dơ</th>
                    <th className="px-2 py-2">Nợ ĐK</th>
                    {days.map((d) => (
                      <th key={d} className="px-2 py-2 text-center">{dayLabel(d)}</th>
                    ))}
                    <th className="px-3 py-2 text-center">Tổng cộng</th>
                    <th className="px-3 py-2 text-center">Chênh kỳ</th>
                    <th className="px-3 py-2 text-center">Nợ CK</th>
                    <th className="px-3 py-2 text-center">Lệch</th>
                  </tr>
                </thead>
                <tbody>
                  {report.categories.map((row) => (
                    // Fragment phải mang key (mỗi loại đồ = 2 dòng Dơ/Sạch), không thì React cảnh báo.
                    <Fragment key={row.category}>
                      <tr className="border-t">
                        <td
                          rowSpan={2}
                          className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white z-10 border-r"
                        >
                          {row.category}
                        </td>
                        <td className="px-2 py-1 text-center text-slate-500">Dơ</td>
                        <td rowSpan={2} className="px-2 py-1 text-center font-semibold text-amber-700">
                          {cell(row.opening_debt)}
                        </td>
                        {days.map((d) => (
                          <td key={d} className="px-2 py-1 text-center">{cell(row.dirty[d] || 0)}</td>
                        ))}
                        <td className="px-3 py-1 text-center font-semibold">{fmt(row.dirty_total)}</td>
                        <td
                          rowSpan={2}
                          className={`px-3 py-1 text-center font-bold ${
                            row.diff_in_period < 0 ? "text-rose-600" : row.diff_in_period > 0 ? "text-emerald-600" : "text-slate-400"
                          }`}
                        >
                          {row.diff_in_period === 0 ? "0" : fmt(row.diff_in_period)}
                        </td>
                        <td rowSpan={2} className="px-3 py-1 text-center font-semibold text-amber-700">
                          <div className="flex items-center justify-center gap-1.5">
                            <span>{cell(row.closing_debt)}</span>
                            <button
                              title="Tạo yêu cầu điều chỉnh công nợ cho loại đồ này"
                              onClick={() => { setCreateRequestCategory(row.category); setShowCreateRequest(true); }}
                              className="text-slate-400 hover:text-green-600 font-normal text-xs"
                            >
                              ✎
                            </button>
                          </div>
                        </td>
                        <td
                          rowSpan={2}
                          className={`px-3 py-1 text-center text-xs ${
                            row.balance_gap === 0 ? "text-slate-300" : "text-rose-600 font-bold"
                          }`}
                          title="Chênh chưa giải thích được: khác 0 nghĩa là dữ liệu bất thường"
                        >
                          {row.balance_gap === 0 ? "✓" : fmt(row.balance_gap)}
                        </td>
                      </tr>
                      <tr className="border-b bg-slate-50/40">
                        <td className="px-2 py-1 text-center text-slate-500">Sạch</td>
                        {days.map((d) => {
                          // Sạch ĐÃ gồm đồ trả nợ (box "Trả nợ", ngoài số kiểm đếm giao) → ghi chú "trả".
                          // Nợ khách là đồ CHƯA giao → KHÔNG nằm trong số Sạch, ghi chú "nợ" màu cam.
                          const clean = row.clean[d] || 0;
                          const returned = row.clean_returned?.[d] || 0;
                          const owed = row.clean_owed?.[d] || 0;
                          const tips = [
                            returned ? `Kiểm đếm giao ${clean - returned} + trả nợ ${returned}` : "",
                            owed ? `Còn nợ ${owed} — chưa giao, không tính trong số Sạch` : "",
                          ].filter(Boolean);
                          return (
                            <td
                              key={d}
                              className={`px-2 py-1 text-center ${owed ? "bg-amber-50" : returned ? "bg-emerald-50" : ""}`}
                              title={tips.length ? tips.join("\n") : undefined}
                            >
                              {owed && !clean ? "0" : cell(clean)}
                              {!!returned && <div className="text-[10px] leading-none text-emerald-600">trả {returned}</div>}
                              {!!owed && <div className="text-[10px] leading-none text-amber-600">nợ {owed}</div>}
                            </td>
                          );
                        })}
                        <td className="px-3 py-1 text-center font-semibold">
                          {fmt(row.clean_total)}
                          {!!row.clean_returned_total && (
                            <div className="text-[10px] font-normal leading-none text-emerald-600">trả {row.clean_returned_total}</div>
                          )}
                          {!!row.clean_owed_total && (
                            <div className="text-[10px] font-normal leading-none text-amber-600">nợ {row.clean_owed_total}</div>
                          )}
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                  {/* Dòng kg — theo ngày hoàn thành đơn */}
                  <tr className="border-t-2 bg-slate-100 font-medium">
                    <td className="px-3 py-2 sticky left-0 bg-slate-100 z-10">Số kg</td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2" />
                    {days.map((d) => (
                      <td key={d} className="px-2 py-2 text-center">
                        {report.weight_by_date[d] ? fmt(report.weight_by_date[d]) : ""}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center">{fmt(report.weight_total)}</td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </table>
            </div>
            {report.categories.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Không có đơn kiểm đếm nào của khách này trong khoảng thời gian đã chọn.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-2 text-xs text-slate-500">
                <span>
                  <span className="font-medium text-emerald-600">trả n</span>: đồ trả nợ — <b>đã gồm</b> trong số Sạch
                </span>
                <span>
                  <span className="font-medium text-amber-600">nợ n</span>: giao thiếu, hẹn trả sau — <b>không</b> nằm trong số Sạch
                </span>
              </div>
            )}
          </div>

          {/* Chi tiết bút toán — truy vết nợ ở đơn nào, trả ở đơn nào */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <button
              onClick={() => setShowLogs((v) => !v)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold text-slate-800"
            >
              <span>Chi tiết phát sinh nợ – trả ({logs.length})</span>
              <span className="text-slate-400">{showLogs ? "▲" : "▼"}</span>
            </button>
            {showLogs && (
              <div className="overflow-x-auto border-t">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Ngày</th>
                      <th className="px-3 py-2 text-left">Loại đồ</th>
                      <th className="px-3 py-2 text-center">SL</th>
                      <th className="px-3 py-2 text-left">Loại phát sinh</th>
                      <th className="px-3 py-2 text-center">Đơn</th>
                      <th className="px-3 py-2 text-left">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => (
                      <tr key={l.id} className="border-t">
                        <td className="px-3 py-2 text-slate-500">
                          {toDMY(String(l.created_at).slice(0, 10))}
                        </td>
                        <td className="px-3 py-2">{l.category}</td>
                        <td
                          className={`px-3 py-2 text-center font-semibold ${
                            l.quantity > 0 ? "text-rose-600" : "text-emerald-600"
                          }`}
                        >
                          {l.quantity > 0 ? `+${l.quantity}` : l.quantity}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              l.entry_type === ITEM_DEBT_ENTRY_TYPE.OWE
                                ? "bg-amber-100 text-amber-700"
                                : l.entry_type === ITEM_DEBT_ENTRY_TYPE.RETURN
                                ? "bg-emerald-100 text-emerald-700"
                                : l.entry_type === ITEM_DEBT_ENTRY_TYPE.LOST
                                ? "bg-rose-100 text-rose-700"
                                : l.entry_type === ITEM_DEBT_ENTRY_TYPE.NON_STANDARD
                                ? "bg-orange-100 text-orange-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {ITEM_DEBT_ENTRY_LABEL[l.entry_type] || l.entry_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-slate-500">
                          {l.order_code || (l.order_id ? `#${l.order_id}` : "—")}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{l.note || ""}</td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                          Không có phát sinh nợ – trả trong kỳ.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showCreateRequest && !!customerId && (
        <CreateDebtAdjustmentRequestModal
          open={showCreateRequest}
          customerId={Number(customerId)}
          customerName={customerName}
          categoryOptions={(report?.categories || []).map((c) => ({
            category: c.category,
            category_id: c.category_id,
            closing_debt: c.closing_debt,
          }))}
          initialCategory={createRequestCategory || undefined}
          onClose={() => setShowCreateRequest(false)}
          onCreated={() => void loadReport()}
        />
      )}
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: string; unit?: string; tone?: string }> = ({
  label, value, unit, tone,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <div className="text-xs text-slate-500 mb-1">{label}</div>
    <div
      className={`text-2xl font-bold ${
        tone === "rose" ? "text-rose-600" : tone === "amber" ? "text-amber-600" : "text-slate-800"
      }`}
    >
      {value} <span className="text-sm font-normal text-slate-400">{unit}</span>
    </div>
  </div>
);

const Chip: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => {
  const toneCls: Record<string, string> = {
    slate: "bg-slate-100 text-slate-700",
    amber: "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    orange: "bg-orange-100 text-orange-700",
    violet: "bg-violet-100 text-violet-700",
  };
  return (
    <span className={`px-3 py-1 rounded-full ${toneCls[tone] || toneCls.slate}`}>
      {label}: <b>{value > 0 ? `+${fmt(value)}` : fmt(value)}</b>
    </span>
  );
};

export default ReconciliationReportPage;
